import * as React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Palette, Plus, Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import { BRAND_STARTERS, makeTerms, makeTheme, type BrandStarter } from "@/data/config";
import { usePortal } from "@/features/portal/portal-context";
import { brandPath } from "@/features/portal/navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Derive up-to-two-letter initials from a brand name. */
function deriveInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/** Label + optional hint wrapper, matching the portal's field vocabulary. */
function Field({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-[7px]">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && <p className="text-[11.5px] text-text-3">{hint}</p>}
    </div>
  );
}

/** Mini swatch row shown on each starter card. */
function PaletteDots({ theme }: { theme: BrandStarter["theme"] }) {
  const keys = ["background", "surface", "primary", "accent", "text"] as const;
  return (
    <div className="flex gap-[5px]">
      {keys.map((k) => (
        <span
          key={k}
          className="size-[18px] rounded-md border border-[rgba(255,255,255,.14)]"
          style={{ background: theme[k] }}
        />
      ))}
    </div>
  );
}

export function CreateBrandPage() {
  const { brands, commitBrand } = usePortal();
  const navigate = useNavigate();

  const [name, setName] = React.useState("");
  const [initialsEdited, setInitialsEdited] = React.useState(false);
  const [initials, setInitials] = React.useState("");
  const [starterId, setStarterId] = React.useState(BRAND_STARTERS[0]!.id);
  const [logoText, setLogoText] = React.useState("");

  const starter = BRAND_STARTERS.find((s) => s.id === starterId) ?? BRAND_STARTERS[0]!;
  const shownInitials = (initialsEdited ? initials : deriveInitials(name)) || "★";
  const canCreate = name.trim().length > 0;
  const avatarGrad = `linear-gradient(135deg, ${starter.theme.primary}, ${starter.theme.accent})`;

  function cancel() {
    // Back to the brand you came from (the first one you have access to).
    const first = brands[0];
    navigate(first ? brandPath(first.id) : "/");
  }

  const [submitting, setSubmitting] = React.useState(false);

  async function submit() {
    if (!canCreate || submitting) return;
    const finalInitials = (initialsEdited ? initials : deriveInitials(name)) || "BR";
    const trimmedName = name.trim();
    setSubmitting(true);
    // The chosen starter palette seeds the new brand's default (live) theme; the
    // backend also adds its other sample themes to tune afterwards in Theming.
    const newId = await commitBrand({
      name: trimmedName,
      initials: finalInitials.slice(0, 2),
      theme: makeTheme({ ...starter.theme, logoText: logoText.trim() || trimmedName }),
      terms: makeTerms({}),
    });
    setSubmitting(false);
    if (newId) navigate(brandPath(newId));
  }

  // The first brand is a required step (no brand to go "back" to), so the
  // back/cancel escape hatches only appear once the account already has a brand.
  const hasBrands = brands.length > 0;

  return (
    <div className="max-w-[720px]">
      {hasBrands && (
        <button
          onClick={cancel}
          className="mb-[18px] inline-flex cursor-pointer items-center gap-[7px] text-[13px] font-semibold text-text-3"
        >
          <ArrowLeft className="size-4" /> Back
        </button>
      )}

      <h1 className="mb-1.5 text-2xl font-extrabold tracking-[-.02em]">Create a brand</h1>
      <p className="mb-7 max-w-[560px] text-sm text-muted-foreground">
        Spin up a new white-labelled app. Pick a name and a starting palette — you can fine-tune
        every color, the logo and terminology afterwards.
      </p>

      {/* Identity */}
      <Card className="mb-4">
        <CardContent>
          <div className="mb-[18px] flex items-center gap-2 text-[13px] font-bold">
            <Sparkles className="size-4 text-brand" /> Identity
          </div>

          <div className="flex items-start gap-[18px]">
            <div className="shrink-0 text-center">
              <div
                className="grid size-[68px] place-items-center rounded-[18px] text-[26px] font-extrabold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,.14)]"
                style={{ background: avatarGrad }}
              >
                {shownInitials}
              </div>
              <div className="mt-2 text-[10.5px] text-text-3">Preview</div>
            </div>

            <div className="grid flex-1 grid-cols-[1fr_110px] gap-3.5">
              <Field label="Brand name" hint="Shown across the portal and to your players." htmlFor="brand-name">
                <Input
                  id="brand-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Nova Play"
                  autoFocus
                />
              </Field>
              <Field label="Initials" hint="Auto from name." htmlFor="brand-initials">
                <Input
                  id="brand-initials"
                  value={initialsEdited ? initials : deriveInitials(name)}
                  onChange={(e) => {
                    setInitialsEdited(true);
                    setInitials(e.target.value.toUpperCase().slice(0, 2));
                  }}
                  maxLength={2}
                  placeholder="NP"
                  className="text-center font-bold tracking-[.08em]"
                />
              </Field>
            </div>
          </div>

          <div className="mt-4">
            <Field
              label="Logo wordmark"
              hint="Optional. Defaults to the brand name; upload an image later in Theming."
              htmlFor="brand-logo"
            >
              <Input
                id="brand-logo"
                value={logoText}
                onChange={(e) => setLogoText(e.target.value)}
                placeholder={name || "brand name"}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      {/* Starting palette */}
      <Card>
        <CardContent>
          <div className="mb-1 flex items-center gap-2 text-[13px] font-bold">
            <Palette className="size-4 text-brand" /> Starting palette
          </div>
          <div className="mb-4 text-[11.5px] text-text-3">
            A theme to build on. Change any color afterwards in Theming.
          </div>

          <div className="grid grid-cols-2 gap-3">
            {BRAND_STARTERS.map((s) => {
              const active = s.id === starterId;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStarterId(s.id)}
                  className={cn(
                    "flex cursor-pointer flex-col gap-3 rounded-[13px] border p-3.5 text-left transition-all",
                    active
                      ? "border-brand bg-panel-2 ring-[3px] ring-ring"
                      : "border-border bg-field",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <PaletteDots theme={s.theme} />
                    <span
                      className={cn(
                        "grid size-[18px] shrink-0 place-items-center rounded-full border",
                        active ? "border-brand bg-brand" : "border-border-strong",
                      )}
                    >
                      {active && <Check className="size-3 text-white" />}
                    </span>
                  </div>
                  <div>
                    <div className="text-[13.5px] font-bold text-foreground">{s.name}</div>
                    <div className="mt-0.5 text-[11.5px] text-text-3">{s.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="mt-[22px] flex items-center justify-between">
        <div className="text-xs text-text-3">
          {brands.length > 0
            ? `${brands.length} brand${brands.length > 1 ? "s" : ""} in your account`
            : "Your first brand"}
        </div>
        <div className="flex items-center gap-2.5">
          {hasBrands && (
            <Button variant="ghost" onClick={cancel}>
              Cancel
            </Button>
          )}
          <Button
            onClick={submit}
            disabled={!canCreate || submitting}
            className={cn((!canCreate || submitting) && "opacity-50 shadow-none")}
          >
            <Plus className="size-4" /> {submitting ? "Creating…" : "Create brand"}
          </Button>
        </div>
      </div>
    </div>
  );
}
